// A chart box drawn on a slide (SD): one SVG in the slide's own pixels (the box's width and height at 1600 by 900), so
// it is crisp at every size a slide shows (the editor, the strip, present, the viewer, a phone, a printed page). Its
// colours are the deck's theme colours, its words the deck's fonts at the box's size; marks are thin and quiet (bars
// with rounded ends at their tips, 2px gaps in the slide's colour between touching marks, hairline gridlines), and
// only numbers the chart was given are drawn: one with none says "Add your data".

import { useId, type ReactNode } from "react";
import { chartHasData, formatValue, isRound, legendOf, niceRange, niceScale, parseValue, seriesColor, type SlideChart } from "../../../../shared/slideChart";
import type { Palette } from "../../../../shared/slideDesign";
import { smoothPath } from "../../../charts/charts";
import { readableOn } from "../../../themes/themes";
import { r2 } from "../../../../shared/slidePlace";

/** The size a chart's words are drawn at when its box says none (axis labels, the legend), at 1600 by 900. */
export const CHART_SIZE = 22;

export interface ChartLook {
  palette: Palette;
  /** the words' font, and the title's, as CSS */
  font: string;
  titleFont: string;
  size: number;
}

/** What a chart says instead of numbers, when it has none it can draw. */
export interface ChartNote {
  title: string;
  body?: string;
}

const tw = (s: string, size: number) => s.length * size * 0.56;
const cut = (s: string, size: number, room: number) => {
  if (tw(s, size) <= room) return s;
  const n = Math.max(1, Math.floor(room / (size * 0.56)) - 1);
  return s.slice(0, n).trimEnd() + "…";
};

/** A hex colour with an alpha, for SVG. */
const alpha = (hex: string, a: number) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return `rgb(${r} ${g} ${b} / ${a})`;
};

/** A bar's path: square at the baseline, its far end rounded (r), across (horizontal) or up. */
function barPath(x: number, y: number, w: number, h: number, r: number, end: "top" | "bottom" | "right" | "left"): string {
  const k = Math.max(0, Math.min(r, w / 2, h / 2));
  if (!k) return `M${r2(x)},${r2(y)}h${r2(w)}v${r2(h)}h${r2(-w)}Z`;
  const X = r2(x);
  const Y = r2(y);
  switch (end) {
    case "top":
      return `M${X},${r2(y + h)}V${r2(y + k)}Q${X},${Y} ${r2(x + k)},${Y}H${r2(x + w - k)}Q${r2(x + w)},${Y} ${r2(x + w)},${r2(y + k)}V${r2(y + h)}Z`;
    case "bottom":
      return `M${X},${Y}V${r2(y + h - k)}Q${X},${r2(y + h)} ${r2(x + k)},${r2(y + h)}H${r2(x + w - k)}Q${r2(x + w)},${r2(y + h)} ${r2(x + w)},${r2(y + h - k)}V${Y}Z`;
    case "right":
      return `M${X},${Y}H${r2(x + w - k)}Q${r2(x + w)},${Y} ${r2(x + w)},${r2(y + k)}V${r2(y + h - k)}Q${r2(x + w)},${r2(y + h)} ${r2(x + w - k)},${r2(y + h)}H${X}Z`;
    default:
      return `M${r2(x + w)},${Y}H${r2(x + k)}Q${X},${Y} ${X},${r2(y + k)}V${r2(y + h - k)}Q${X},${r2(y + h)} ${r2(x + k)},${r2(y + h)}H${r2(x + w)}Z`;
  }
}

/** The chart, as an SVG of w by h. note: what to say in place of numbers (a sheet that is gone). */
export function ChartSvg({ chart: c, w, h, look, note }: { chart: SlideChart; w: number; h: number; look: ChartLook; note?: ChartNote | null }) {
  const gid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const p = look.palette;
  const s = look.size;
  const ink = p.text;
  const soft = p.muted;
  const grid = alpha(p.text, 0.12);
  const base = alpha(p.text, 0.32);
  const els: ReactNode[] = [];
  let top = 0;
  let bottom = h;
  let left = 0;
  let right = w;

  // the title, over it
  if (c.title) {
    const ts = Math.round(s * 1.55);
    els.push(
      <text key="title" x={w / 2} y={ts} textAnchor="middle" fontFamily={look.titleFont} fontSize={ts} fontWeight={600} fill={ink}>
        {cut(c.title, ts, w)}
      </text>,
    );
    top += ts * 1.7;
  }

  const empty = !chartHasData(c);
  if (note || empty) {
    const t = note?.title ?? "Add your data";
    const body = note ? note.body : "Double-click the chart, or Edit data in its bar.";
    // the words fit the box, however small: a size that fits across, the quiet bars only where there is room for them
    const room = h - top;
    const ts = Math.max(12, Math.min(Math.round(s * 1.15), Math.floor((w - 32) / (t.length * 0.56))));
    const bs = Math.max(11, Math.min(Math.round(s * 0.85), ts));
    const bars = room >= ts * 2 + 110;
    const cy = top + room / 2 + (bars ? 0 : -ts * 0.4);
    els.push(
      <g key="empty">
        <rect x={1.5} y={top + 1.5} width={w - 3} height={room - 3} rx={18} fill="none" stroke={alpha(p.text, 0.22)} strokeWidth={2} strokeDasharray="10 8" />
        {bars && (
          <g transform={`translate(${w / 2 - 33} ${cy - 70})`} fill={alpha(p.text, 0.18)}>
            <rect x={0} y={28} width={16} height={36} rx={3} />
            <rect x={25} y={10} width={16} height={54} rx={3} />
            <rect x={50} y={40} width={16} height={24} rx={3} />
          </g>
        )}
        <text x={w / 2} y={cy + (bars ? 22 : ts * 0.36)} textAnchor="middle" fontFamily={look.font} fontSize={ts} fontWeight={600} fill={ink}>
          {t}
        </text>
        {body && room >= ts * 2 + bs * 2 && (
          <text x={w / 2} y={cy + (bars ? 22 : ts * 0.36) + ts * 1.3} textAnchor="middle" fontFamily={look.font} fontSize={bs} fill={soft}>
            {cut(body, bs, w - 40)}
          </text>
        )}
      </g>,
    );
    return (
      <svg className="sd-chart" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={c.title ?? "Chart"}>
        {els}
      </svg>
    );
  }

  const round = isRound(c.type);
  const series = round ? c.series.slice(0, 1) : c.series;
  let pieAt: number | null = null;
  let pieR: number | null = null;
  const color = (i: number) => seriesColor(c, i, p, round ? undefined : series[i]?.color);

  // the legend: the series' names (a pie's slices) with their colours, where it is said to sit
  const place = legendOf(c);
  const items = round ? c.cats.map((name, i) => ({ name: name || `Slice ${i + 1}`, color: seriesColor(c, i, p), on: (series[0]?.values[i] ?? null) !== null })).filter((x) => x.on) : series.map((x, i) => ({ name: x.name, color: color(i), line: c.type === "line" || c.type === "scatter" || (c.type === "combo" && x.line) }));
  if (place !== "none" && items.length) {
    const ls = Math.round(s * 0.9);
    const sw = ls * 0.72;
    if (place === "bottom" || place === "top") {
      const widths = items.map((it) => sw + 10 + tw(it.name, ls) + 28);
      const rows: { i: number; x: number }[][] = [[]];
      let x = 0;
      widths.forEach((wd, i) => {
        if (x + wd > w && rows[rows.length - 1].length) {
          rows.push([]);
          x = 0;
        }
        rows[rows.length - 1].push({ i, x });
        x += wd;
      });
      const lh = ls * 1.6;
      const y0 = place === "bottom" ? h - rows.length * lh + ls * 0.3 : top;
      rows.forEach((row, ri) => {
        const width = row.length ? row[row.length - 1].x + widths[row[row.length - 1].i] - 28 : 0;
        const ox = (w - width) / 2;
        row.forEach(({ i, x: lx }) => {
          const it = items[i];
          const y = y0 + ri * lh;
          els.push(
            <g key={`lg${i}`}>
              {"line" in it && it.line ? <rect x={ox + lx} y={y + ls * 0.36} width={sw} height={Math.max(3, ls * 0.16)} rx={2} fill={it.color} /> : <rect x={ox + lx} y={y + ls * 0.08} width={sw} height={sw} rx={sw * 0.22} fill={it.color} />}
              <text x={ox + lx + sw + 10} y={y + ls * 0.72} fontFamily={look.font} fontSize={ls} fill={soft}>
                {it.name}
              </text>
            </g>,
          );
        });
      });
      if (place === "bottom") bottom -= rows.length * lh + ls * 0.6;
      else top += rows.length * lh + ls * 0.4;
    } else {
      const lh = ls * 1.7;
      const colW = Math.min(w * 0.34, Math.max(...items.map((it) => sw + 12 + tw(it.name, ls))) + 8);
      const y0 = top + (h - top - items.length * lh) / 2;
      // a pie stands beside its legend, the two in the middle together
      let lx = place === "right" ? w - colW : 0;
      if (round) {
        const R0 = Math.max(10, Math.min(w - colW - s * 2, h - top) / 2 - 4);
        const group = R0 * 2 + s * 2.4 + colW;
        const ox = Math.max(0, (w - group) / 2);
        lx = place === "right" ? ox + R0 * 2 + s * 2.4 : ox;
        pieAt = place === "right" ? ox + R0 : ox + colW + s * 2.4 + R0;
        pieR = R0;
      }
      items.forEach((it, i) => {
        const y = y0 + i * lh;
        els.push(
          <g key={`lg${i}`}>
            {"line" in it && it.line ? <rect x={lx} y={y + ls * 0.36} width={sw} height={Math.max(3, ls * 0.16)} rx={2} fill={it.color} /> : <rect x={lx} y={y + ls * 0.08} width={sw} height={sw} rx={sw * 0.22} fill={it.color} />}
            <text x={lx + sw + 12} y={y + ls * 0.72} fontFamily={look.font} fontSize={ls} fill={soft}>
              {cut(it.name, ls, colW - sw - 12)}
            </text>
          </g>,
        );
      });
      if (place === "right") right -= colW + s;
      else left += colW + s;
    }
  }

  const fmt = (v: number, compact = false) => formatValue(v, c, compact);
  const ls = Math.round(s * 0.86);

  // ---------------------------------------------------------------------------
  // a pie or a doughnut: one series, a slice a category, from twelve o'clock, clockwise
  if (round) {
    const vals = (series[0]?.values ?? []).map((v) => (v !== null && v > 0 ? v : 0));
    const total = vals.reduce((a, b) => a + b, 0);
    const cx = pieAt ?? (left + right) / 2;
    const cy = (top + bottom) / 2;
    const R = Math.max(10, (pieR ?? Math.min(right - left, bottom - top) / 2) - (c.labels ? s * 0.4 : 4));
    const r0 = c.type === "doughnut" ? R * 0.56 : 0;
    let a = -Math.PI / 2;
    const gap = Math.max(1.5, R * 0.012);
    vals.forEach((v, i) => {
      if (!v || !total) return;
      const sweep = (v / total) * Math.PI * 2;
      const a1 = a + sweep;
      const large = sweep > Math.PI ? 1 : 0;
      const pt = (rad: number, ang: number) => `${r2(cx + rad * Math.cos(ang))},${r2(cy + rad * Math.sin(ang))}`;
      const full = sweep >= Math.PI * 2 - 1e-6;
      const d = full
        ? r0
          ? `M${pt(R, a)}A${R},${R} 0 1 1 ${pt(R, a + Math.PI)}A${R},${R} 0 1 1 ${pt(R, a)}M${pt(r0, a)}A${r0},${r0} 0 1 0 ${pt(r0, a + Math.PI)}A${r0},${r0} 0 1 0 ${pt(r0, a)}Z`
          : `M${pt(R, a)}A${R},${R} 0 1 1 ${pt(R, a + Math.PI)}A${R},${R} 0 1 1 ${pt(R, a)}Z`
        : r0
          ? `M${pt(R, a)}A${R},${R} 0 ${large} 1 ${pt(R, a1)}L${pt(r0, a1)}A${r0},${r0} 0 ${large} 0 ${pt(r0, a)}Z`
          : `M${r2(cx)},${r2(cy)}L${pt(R, a)}A${R},${R} 0 ${large} 1 ${pt(R, a1)}Z`;
      const fill = seriesColor(c, i, p);
      els.push(
        <path key={`sl${i}`} d={d} fill={fill} stroke={p.background} strokeWidth={vals.filter(Boolean).length > 1 ? gap * 2 : 0} strokeLinejoin="round" fillRule="evenodd">
          <title>{`${c.cats[i] || `Slice ${i + 1}`}: ${fmt(v)}`}</title>
        </path>,
      );
      if (c.labels && sweep > 0.32) {
        const mid = a + sweep / 2;
        const lr = r0 ? (R + r0) / 2 : R * 0.64;
        const lx = cx + lr * Math.cos(mid);
        const ly = cy + lr * Math.sin(mid);
        els.push(
          <text key={`sv${i}`} x={r2(lx)} y={r2(ly + ls * 0.36)} textAnchor="middle" fontFamily={look.font} fontSize={ls} fontWeight={600} fill={readableOn(fill) === "#ffffff" ? "#FFFFFF" : "#111317"}>
            {fmt(v)}
          </text>,
        );
      }
      a = a1;
    });
    return (
      <svg className="sd-chart" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={c.title ?? "Chart"}>
        {els}
      </svg>
    );
  }

  // ---------------------------------------------------------------------------
  // charts with axes
  const horizontal = c.type === "bar";
  const scatter = c.type === "scatter";
  const xs = scatter ? c.cats.map((x) => parseValue(x).n) : [];
  if (scatter && !xs.some((x) => x !== null)) {
    return <ChartSvg chart={c} w={w} h={h} look={look} note={{ title: "Its X values are not numbers", body: "A scatter chart's first column holds the X values, as numbers." }} />;
  }
  const n = c.cats.length;
  const colSeries = series.map((x, i) => ({ x, i })).filter(({ x }) => c.type === "column" || c.type === "bar" || (c.type === "combo" && !x.line));
  const lineSeries = series.map((x, i) => ({ x, i })).filter(({ x }) => c.type === "line" || c.type === "area" || c.type === "scatter" || (c.type === "combo" && x.line));
  const stacked = !!c.stacked && (c.type === "column" || c.type === "bar" || c.type === "area");
  // the values' reach: stacks sum their positives and negatives apart
  let lo = Infinity;
  let hi = -Infinity;
  for (let j = 0; j < n; j++) {
    if (stacked) {
      let pos = 0;
      let neg = 0;
      for (const x of series) {
        const v = x.values[j];
        if (v === null || v === undefined) continue;
        if (v >= 0) pos += v;
        else neg += v;
      }
      hi = Math.max(hi, pos);
      lo = Math.min(lo, neg);
    } else
      for (const x of series) {
        const v = x.values[j];
        if (v === null || v === undefined) continue;
        hi = Math.max(hi, v);
        lo = Math.min(lo, v);
      }
  }
  if (!Number.isFinite(lo)) lo = 0;
  if (!Number.isFinite(hi)) hi = 0;
  const scale = niceScale(lo, hi, horizontal ? 5 : Math.max(3, Math.min(6, Math.round((bottom - top) / (s * 4)))));
  const ticks: number[] = [];
  for (let v = scale.lo; v <= scale.hi + scale.step * 0.001; v += scale.step) ticks.push(Math.round(v * 1e9) / 1e9);
  const tickW = Math.max(...ticks.map((t) => tw(fmt(t, true), ls)));

  // the axes' titles
  if (c.yTitle) {
    const ys = Math.round(s * 0.86);
    if (horizontal) {
      els.push(
        <text key="yt" x={(left + right) / 2} y={bottom - ys * 0.2} textAnchor="middle" fontFamily={look.font} fontSize={ys} fontWeight={600} fill={soft}>
          {cut(c.yTitle, ys, right - left)}
        </text>,
      );
      bottom -= ys * 1.6;
    } else {
      els.push(
        <text key="yt" transform={`translate(${left + ys} ${(top + bottom) / 2}) rotate(-90)`} textAnchor="middle" fontFamily={look.font} fontSize={ys} fontWeight={600} fill={soft}>
          {cut(c.yTitle, ys, bottom - top)}
        </text>,
      );
      left += ys * 1.7;
    }
  }
  if (c.xTitle) {
    const xs2 = Math.round(s * 0.86);
    if (horizontal) {
      els.push(
        <text key="xt" transform={`translate(${left + xs2} ${(top + bottom) / 2}) rotate(-90)`} textAnchor="middle" fontFamily={look.font} fontSize={xs2} fontWeight={600} fill={soft}>
          {cut(c.xTitle, xs2, bottom - top)}
        </text>,
      );
      left += xs2 * 1.7;
    } else {
      els.push(
        <text key="xt" x={(left + right) / 2} y={bottom - xs2 * 0.2} textAnchor="middle" fontFamily={look.font} fontSize={xs2} fontWeight={600} fill={soft}>
          {cut(c.xTitle, xs2, right - left)}
        </text>,
      );
      bottom -= xs2 * 1.6;
    }
  }

  // room for the labels along each axis
  const catW = horizontal ? Math.min((right - left) * 0.32, Math.max(...c.cats.map((x) => tw(x, ls)), 0) + s * 0.6) : 0;
  const plot = horizontal
    ? { x0: left + catW, x1: right - (c.labels ? tw(fmt(hi), ls) + 8 : tickW / 2), y0: top + 4, y1: bottom - ls * 1.7 }
    : { x0: left + tickW + s * 0.6, x1: right - 4, y0: top + (c.labels ? ls * 1.4 : ls * 0.6), y1: bottom - (n && c.cats.some(Boolean) ? ls * 1.9 : ls * 0.6) };
  const val = (v: number) => (horizontal ? plot.x0 + ((v - scale.lo) / (scale.hi - scale.lo)) * (plot.x1 - plot.x0) : plot.y1 - ((v - scale.lo) / (scale.hi - scale.lo)) * (plot.y1 - plot.y0));
  const zero = val(Math.max(scale.lo, Math.min(scale.hi, 0)));

  // the value axis: its gridlines and numbers
  ticks.forEach((t, i) => {
    const at = val(t);
    if (c.grid !== false && t !== 0) els.push(horizontal ? <line key={`g${i}`} x1={at} x2={at} y1={plot.y0} y2={plot.y1} stroke={grid} strokeWidth={1.5} /> : <line key={`g${i}`} x1={plot.x0} x2={plot.x1} y1={at} y2={at} stroke={grid} strokeWidth={1.5} />);
    els.push(
      horizontal ? (
        <text key={`t${i}`} x={at} y={plot.y1 + ls * 1.3} textAnchor="middle" fontFamily={look.font} fontSize={ls} fill={soft}>
          {fmt(t, true)}
        </text>
      ) : (
        <text key={`t${i}`} x={plot.x0 - s * 0.5} y={at + ls * 0.34} textAnchor="end" fontFamily={look.font} fontSize={ls} fill={soft} style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmt(t, true)}
        </text>
      ),
    );
  });

  // the categories: a band each (a scatter chart's X values on an axis of their own)
  let xAt: (j: number) => number;
  let band = 0;
  if (scatter) {
    const known = xs.filter((x): x is number => x !== null);
    const lo0 = Math.min(...known);
    const hi0 = Math.max(...known);
    const xr = niceRange(lo0 - (hi0 - lo0) * 0.04, hi0 + (hi0 - lo0) * 0.04, 6);
    const span = xr.hi - xr.lo || 1;
    xAt = (j) => plot.x0 + (((xs[j] ?? xr.lo) - xr.lo) / span) * (plot.x1 - plot.x0);
    for (let v = xr.lo, k = 0; v <= xr.hi + xr.step * 0.001; v += xr.step, k++) {
      const at = plot.x0 + ((v - xr.lo) / span) * (plot.x1 - plot.x0);
      if (c.grid !== false) els.push(<line key={`xg${k}`} x1={at} x2={at} y1={plot.y0} y2={plot.y1} stroke={grid} strokeWidth={1.5} />);
      els.push(
        <text key={`xl${k}`} x={at} y={plot.y1 + ls * 1.4} textAnchor="middle" fontFamily={look.font} fontSize={ls} fill={soft}>
          {formatValue(Math.round(v * 1e9) / 1e9, {}, true)}
        </text>,
      );
    }
  } else {
    band = ((horizontal ? plot.y1 - plot.y0 : plot.x1 - plot.x0) || 1) / Math.max(1, n);
    xAt = (j) => (horizontal ? plot.y0 : plot.x0) + band * (j + 0.5);
    // labels that would run into each other are thinned to every second, third...
    const widest = Math.max(...c.cats.map((x) => tw(x, ls)), 1);
    const every = horizontal ? Math.max(1, Math.ceil((ls * 1.3) / band)) : Math.max(1, Math.ceil((Math.min(widest, band * 2.4) + s * 0.8) / band));
    c.cats.forEach((cat, j) => {
      if (!cat || j % every) return;
      els.push(
        horizontal ? (
          <text key={`c${j}`} x={plot.x0 - s * 0.5} y={xAt(j) + ls * 0.34} textAnchor="end" fontFamily={look.font} fontSize={ls} fill={soft}>
            {cut(cat, ls, catW - s * 0.5)}
          </text>
        ) : (
          <text key={`c${j}`} x={xAt(j)} y={plot.y1 + ls * 1.4} textAnchor="middle" fontFamily={look.font} fontSize={ls} fill={soft}>
            {cut(cat, ls, band * every - s * 0.4)}
          </text>
        ),
      );
    });
  }

  // columns and bars: side by side in each band (or stacked), square at the baseline, rounded at their tips
  if (colSeries.length && !scatter) {
    const m = stacked ? 1 : colSeries.length;
    const cluster = band * (m > 1 ? 0.74 : 0.56);
    const gap = Math.max(2, s * 0.12);
    const bw = Math.max(2, Math.min((cluster - gap * (m - 1)) / m, s * 5));
    const used = bw * m + gap * (m - 1);
    const rad = Math.min(s * 0.3, bw / 3);
    for (let j = 0; j < n; j++) {
      let pos = 0;
      let neg = 0;
      const stack = colSeries.filter(({ x }) => x.values[j] !== null && x.values[j] !== undefined);
      colSeries.forEach(({ x, i }, k) => {
        const v = x.values[j];
        if (v === null || v === undefined) return;
        const start = stacked ? (v >= 0 ? pos : neg) : 0;
        const end = start + v;
        if (stacked) {
          if (v >= 0) pos = end;
          else neg = end;
        }
        const a = val(start);
        const b = val(end);
        const off = xAt(j) - used / 2 + (stacked ? 0 : k * (bw + gap));
        const isTop = !stacked || stack[stack.length - 1]?.x === x || (v < 0 && stack.filter(({ x: y }) => (y.values[j] ?? 0) < 0).pop()?.x === x);
        // a 2px gap in the slide's colour between stacked pieces
        const g = stacked && !isTop ? gap : 0;
        const len = Math.max(0, Math.abs(b - a) - g);
        const fill = color(i);
        const d = horizontal ? barPath(v >= 0 ? a : a - len, off, len, bw, isTop ? rad : 0, v >= 0 ? "right" : "left") : barPath(off, v >= 0 ? a - len : a, bw, len, isTop ? rad : 0, v >= 0 ? "top" : "bottom");
        els.push(
          <path key={`b${i}-${j}`} d={d} fill={fill}>
            <title>{`${x.name}${c.cats[j] ? `, ${c.cats[j]}` : ""}: ${fmt(v)}`}</title>
          </path>,
        );
        if (c.labels) {
          const label = fmt(v);
          if (stacked) {
            const fits = horizontal ? len > tw(label, ls) + 12 : len > ls * 1.3 && bw > tw(label, ls) + 6;
            if (fits)
              els.push(
                <text key={`bl${i}-${j}`} x={horizontal ? (a + b) / 2 : off + bw / 2} y={horizontal ? off + bw / 2 + ls * 0.34 : (a + b) / 2 + ls * 0.34} textAnchor="middle" fontFamily={look.font} fontSize={ls} fontWeight={600} fill={readableOn(fill) === "#ffffff" ? "#FFFFFF" : "#111317"}>
                  {label}
                </text>,
              );
          } else
            els.push(
              horizontal ? (
                <text key={`bl${i}-${j}`} x={v >= 0 ? b + 8 : b - 8} y={off + bw / 2 + ls * 0.34} textAnchor={v >= 0 ? "start" : "end"} fontFamily={look.font} fontSize={ls} fontWeight={600} fill={ink}>
                  {label}
                </text>
              ) : (
                <text key={`bl${i}-${j}`} x={off + bw / 2} y={v >= 0 ? b - ls * 0.45 : b + ls * 1.15} textAnchor="middle" fontFamily={look.font} fontSize={ls} fontWeight={600} fill={ink}>
                  {label}
                </text>
              ),
            );
        }
      });
    }
  }

  // lines and areas (a combo's lines over its columns; a scatter's points)
  if (lineSeries.length) {
    const lw = Math.max(3, s * 0.18);
    const dots = scatter || n <= 24;
    const cum = new Array(n).fill(0);
    const order = c.type === "area" && !stacked ? [...lineSeries].reverse() : lineSeries;
    const shapes: ReactNode[] = [];
    const marks: ReactNode[] = [];
    for (const { x, i } of order) {
      const col = color(i);
      const pts: ([number, number] | null)[] = [];
      const basePts: [number, number][] = [];
      for (let j = 0; j < n; j++) {
        const v = x.values[j];
        if (v === null || v === undefined || (scatter && xs[j] === null)) {
          pts.push(null);
          continue;
        }
        const b0 = stacked ? cum[j] : 0;
        const y = val(b0 + v);
        pts.push([r2(xAt(j)), r2(y)]);
        basePts.push([r2(xAt(j)), r2(val(b0))]);
        if (stacked) cum[j] = b0 + v;
      }
      // runs of points with no gap: each drawn on its own
      const runs: [number, number][][] = [];
      let run: [number, number][] = [];
      pts.forEach((pt) => {
        if (pt) run.push(pt);
        else if (run.length) {
          runs.push(run);
          run = [];
        }
      });
      if (run.length) runs.push(run);
      const path = (r: [number, number][]) => (c.smooth && !scatter ? smoothPath(r) : `M${r.map(([a, b]) => `${a},${b}`).join("L")}`);
      if (scatter && c.smooth) runs.splice(0, runs.length, ...runs.map((r) => [...r].sort((a, b) => a[0] - b[0])));
      if (c.type === "area") {
        let k = 0;
        for (const r of runs) {
          const under = basePts.slice(k, k + r.length);
          k += r.length;
          const back = stacked ? [...under].reverse() : [[r[r.length - 1][0], zero] as [number, number], [r[0][0], zero] as [number, number]];
          const top2 = path(r);
          shapes.push(<path key={`a${i}-${k}`} d={`${top2}L${back.map(([a, b]) => `${a},${b}`).join("L")}Z`} fill={stacked ? col : `url(#${gid}a${i})`} fillOpacity={stacked ? 0.88 : 1} />);
        }
        els.push(
          <defs key={`ad${i}`}>
            <linearGradient id={`${gid}a${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={col} stopOpacity={0.42} />
              <stop offset="1" stopColor={col} stopOpacity={0.06} />
            </linearGradient>
          </defs>,
        );
      }
      if (!scatter || c.smooth) for (const [k, r] of runs.entries()) shapes.push(<path key={`l${i}-${k}`} d={path(r)} fill="none" stroke={col} strokeWidth={lw} strokeLinejoin="round" strokeLinecap="round" />);
      pts.forEach((pt, j) => {
        if (!pt) return;
        const v = x.values[j]!;
        if (dots && c.type !== "area") marks.push(<circle key={`d${i}-${j}`} cx={pt[0]} cy={pt[1]} r={scatter ? s * 0.38 : lw * 1.6} fill={col} stroke={p.background} strokeWidth={Math.max(2, lw * 0.7)}><title>{`${x.name}${c.cats[j] ? `, ${c.cats[j]}` : ""}: ${fmt(v)}`}</title></circle>);
        if (c.labels)
          marks.push(
            <text key={`dl${i}-${j}`} x={pt[0]} y={pt[1] - lw * 1.6 - ls * 0.45} textAnchor="middle" fontFamily={look.font} fontSize={ls} fontWeight={600} fill={ink} paintOrder="stroke" stroke={p.background} strokeWidth={ls * 0.22} strokeLinejoin="round">
              {fmt(v)}
            </text>,
          );
      });
    }
    els.push(<g key="lines">{shapes}</g>, <g key="marks">{marks}</g>);
  }

  // the baseline over the marks' square ends
  els.push(horizontal ? <line key="zero" x1={zero} x2={zero} y1={plot.y0} y2={plot.y1} stroke={base} strokeWidth={2} /> : <line key="zero" x1={plot.x0} x2={plot.x1} y1={zero} y2={zero} stroke={base} strokeWidth={2} />);

  return (
    <svg className="sd-chart" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={c.title ?? "Chart"}>
      {els}
    </svg>
  );
}
